import { useEffect, useState } from 'react'
import { Button, Flex, Grid, Input, Select, Tag, Typography } from 'antd'
import { ClearOutlined, SearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import {
  OPERATION_FILTER_KINDS,
  OPERATION_QUERY_MAX,
  OPERATION_FILTER_STATUSES,
  isFilterActive,
  type OperationFilter,
  type OperationKind,
  type OperationFilterStatus,
} from './profileApi'
import { DayRangeBar } from './DayRangeBar'
import type { DayRange } from '@/shared/lib/tenantDay'

/** Yozayotganda har harfda so'rov ketmasin (`SubscribersPage` bilan bir xil son) */
const DEBOUNCE_MS = 350

interface OperationsFilterProps {
  range: DayRange
  onRangeChange: (range: DayRange) => void
  filter: OperationFilter
  onFilterChange: (filter: OperationFilter) => void
  onReset: () => void
}

/**
 * Amallar ro'yxatining filtri — SHARTNOMA §6.
 *
 * <h2>⚠️ SANA — `DayRangeBar` DA</h2>
 * Tenant zonasi qarori va «kelajak to'siladi» qoidasi o'sha faylda,
 * bitta joyda: uni ikki ekran ishlatadi.
 *
 * <h2>⚠️ QIDIRUVDA KECHIKISH</h2>
 * Debouncesiz har harf alohida so'rov bo'lardi va ro'yxat yozish
 * davomida bir necha marta bo'shab-to'lardi.
 *
 * <h2>⚠️ TUR TANLAGICHIDA `GRANT` YO'Q</h2>
 * Egasi qarori, 2026-08-20: «tranzaksiyani ko'ryapti va filtrda paket
 * bo'yicha kerak emas». Paket berishlar endi O'Z bo'limida
 * («Berilgan paketlar»). `OPERATION_KINDS` dan esa u O'CHIRILMAGAN —
 * u yerdan qatorlarning BEZAGI (`myOperations.kind.GRANT`) o'qiladi va
 * o'chirilsa paket qatorlari xom kod bilan chiqardi. Tanlash uchun
 * taklif qilinadigan ro'yxat — {@link OPERATION_FILTER_KINDS}.
 */
export function OperationsFilter({
  range,
  onRangeChange,
  filter,
  onFilterChange,
  onReset,
}: OperationsFilterProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false

  // ⚠️ Maydonning O'Z holati alohida: so'rovga ketadigan qiymat
  // kechikib yangilanadi, kiritilayotgan matn esa darhol ko'rinishi
  // kerak — aks holda maydon «yozishga ulgurmayotgandek» tuyulardi.
  const [text, setText] = useState(filter.q)

  // Tashqaridan tozalanganda (`onReset`) maydon ham bo'shashi shart
  useEffect(() => {
    setText((prev) => (filter.q === '' && prev !== '' ? '' : prev))
  }, [filter.q])

  useEffect(() => {
    if (text.trim() === filter.q.trim()) return
    const timer = setTimeout(() => onFilterChange({ ...filter, q: text.trim() }), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [text, filter, onFilterChange])

  const active = isFilterActive(filter)

  return (
    <Flex vertical gap={10}>
      <DayRangeBar range={range} onChange={onRangeChange} />

      <Flex gap={8} wrap>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          allowClear
          prefix={<SearchOutlined />}
          maxLength={OPERATION_QUERY_MAX}
          placeholder={t('myOperations.filter.searchPlaceholder')}
          // ⚠️ `fontSize: 16` — bezak emas, XULQ: iOS Safari undan kichik
          // matnli maydonga fokus tushganda sahifani kattalashtirib yuboradi.
          style={{ flex: '1 1 220px', minWidth: 180, fontSize: 16 }}
        />
        <Select<OperationFilterStatus | null>
          value={filter.status}
          onChange={(value) => onFilterChange({ ...filter, status: value ?? null })}
          allowClear
          placeholder={t('myOperations.filter.statusAll')}
          style={{ flex: '1 1 150px', minWidth: 140 }}
          options={OPERATION_FILTER_STATUSES.map((value) => ({
            value,
            label: t(`myOperations.result.${value}`),
          }))}
        />
        <Select<OperationKind[]>
          mode="multiple"
          value={[...filter.kinds]}
          onChange={(value) => onFilterChange({ ...filter, kinds: value })}
          allowClear
          maxTagCount="responsive"
          placeholder={t('myOperations.filter.kindAll')}
          style={{ flex: '1 1 220px', minWidth: 180 }}
          options={OPERATION_FILTER_KINDS.map((value) => ({
            value,
            label: t(`myOperations.kind.${value}`),
          }))}
        />
      </Flex>

      {/* Faol filtr KO'RINIB tursin: aks holda bo'sh ro'yxat «amal
          bo'lmagan» bo'lib o'qilardi, sababi esa yuqorida turgan tanlov
          bo'lardi. */}
      {active ? (
        <Flex gap={8} wrap align="center">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('myOperations.filter.active')}
          </Typography.Text>
          {filter.status !== null ? (
            <Tag>{t(`myOperations.result.${filter.status}`)}</Tag>
          ) : null}
          {filter.kinds.map((kind) => (
            <Tag key={kind}>{t(`myOperations.kind.${kind}`)}</Tag>
          ))}
          {filter.q.trim() !== '' ? <Tag>{filter.q.trim()}</Tag> : null}
          <Button
            size={isMobile ? 'middle' : 'small'}
            type="link"
            icon={<ClearOutlined />}
            onClick={() => {
              // Maydonning O'Z holati ham tozalanadi: aks holda kechikish
              // taymeri qolib ketib, tozalangan filtrga eski matnni
              // qaytarardi.
              setText('')
              onReset()
            }}
          >
            {t('myOperations.filter.clear')}
          </Button>
        </Flex>
      ) : null}
    </Flex>
  )
}
